/**
 * PrefsContext — one instance of the browser-local preferences for the whole
 * shell (brief-26 / brief-28).
 *
 * Without this, the rail's theme toggle and the catalogue's pin/view controls
 * would each hold their own copy of the same localStorage record and silently
 * overwrite one another. One provider, one source of truth.
 *
 * Mounted inside KeycloakProvider because the storage key is namespaced by user.
 */

import { createContext, useContext, type ReactNode } from 'react';
import { useConfig } from '../config/ConfigContext';
import { useAuth } from '../auth/KeycloakProvider';
import { useCatalogPrefs } from '../hooks/useCatalogPrefs';

type PrefsValue = ReturnType<typeof useCatalogPrefs>;

const PrefsContext = createContext<PrefsValue | null>(null);

export function PrefsProvider({ children }: { children: ReactNode }) {
  const config = useConfig();
  const { user } = useAuth();
  const value = useCatalogPrefs(config.instance.slug, user?.id ?? null);
  return <PrefsContext.Provider value={value}>{children}</PrefsContext.Provider>;
}

export function usePrefs(): PrefsValue {
  const ctx = useContext(PrefsContext);
  if (!ctx) throw new Error('usePrefs called outside PrefsProvider');
  return ctx;
}
